import React, { useEffect, useState } from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Fab,
    FormControlLabel,
    Switch,
    Tooltip,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { connect } from 'react-redux';
import { showMessage, showError, confirmAction } from '../../../actions/uiActions';
import EmployersApi from '../../../api/employers';

function CreateEmployerAdmin(props) {
    const [busy, setBusy] = useState(false);
    const [feins, setFeins] = useState([]);
    const [form, setForm] = useState({
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        quarterlyReportEnabled: false,
        isSuperAdmin: false,
        is2FAEnabled: false,
    });

    const isEditing = props.form.isEditing;
    const hasSSo = props.form.selectedUser?.hasSso;
    useEffect(() => {
        if (props.form.selectedUser) {
            const {
                firstname,
                lastname,
                email,
                phone,
                quarterlyReportEnabled,
                isSuperAdmin,
                is2FAEnabled,
            } = props.form.selectedUser;
            setForm({
                firstname,
                lastname,
                email,
                phone,
                quarterlyReportEnabled,
                isSuperAdmin,
                is2FAEnabled,
            });
            setFeins(props.form.selectedUser.feins || []);
        } else {
            setForm({
                firstname: '',
                lastname: '',
                email: '',
                phone: '',
                quarterlyReportEnabled: false,
                isSuperAdmin: false,
                is2FAEnabled: false,
            });
            setFeins([]);
        }
    }, [props.form]);

    const selectFeins = (fein) => {
        let updated;

        if (feins.includes(fein)) {
            updated = feins.filter((each) => each !== fein);
        } else {
            updated = [...feins, fein];
        }

        if (!updated.length) {
            props.showError('You must select at least one entity.');
        } else {
            setFeins(updated);
        }
    };

    const handleInputChange = (evt) => {
        const regex = /^[0-9]+$/;
        const { name, value } = evt.target;

        if (name === 'phone' && !regex.test(value) && value !== '') return;

        setForm((prev) => ({
            ...prev,
            [name]: value.trim(),
        }));
    };

    const handleSwitchChange = (evt) => {
        const { name, checked } = evt.target;

        setForm((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const reset = () => {
        setBusy(false);
        setFeins([]);
        setForm({
            firstname: '',
            lastname: '',
            email: '',
            phone: '',
            quarterlyReportEnabled: false,
            is2FAEnabled: false,
        });
        props.toggleForm(null);
    };

    const confirm2faDisable = () =>
        props.confirmAction(
            "You are about to disable this user's 2FA. Do you wish to proceed?",
            () => add({ confirmed: true })
        );

    const add = async ({ confirmed = false }) => {
        try {
            const requiredFields = ['firstname', 'lastname', 'email', 'phone'];
            if (requiredFields.some((field) => form[field] === '')) {
                props.showError('All fields are required');
                return;
            }

            if (feins.length === 0) {
                props.showError('Select at least one entity');
                return;
            }

            const payload = {
                ...form,
                email: form.email.toLowerCase(),
                feins,
                domain: props.domainId,
            };

            setBusy(true);

            if (isEditing) {
                if (
                    props.form.selectedUser.is2FAEnabled &&
                    !payload.is2FAEnabled &&
                    !confirmed
                ) {
                    confirm2faDisable();
                    setBusy(false);
                    return;
                }
                delete payload.domain;
                await EmployersApi.updateAdmin(props.form.selectedUser._id, payload);
                props.showMessage('Admin user updated successfully.');
            } else {
                delete payload.is2FAEnabled
                await EmployersApi.createEmployerAdmin(payload);
                props.showMessage('Admin user created successfully.');
            }
            await props.getNewAdmin(props.domainId);

            reset();
        } catch (error) {
            setBusy(false);
            props.showError(error);
        }
    };

    const renderAddForm = () => (
        <Dialog open={true}>
            <DialogTitle>
                {isEditing ? 'Edit employer admin' : 'Create new employer admin'}
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Complete the information below in order to create a new admin.
                </DialogContentText>

                <div className='form-group nice'>
                    <label>First Name:</label>
                    <input
                        type='text'
                        className='form-control'
                        value={form.firstname}
                        name='firstname'
                        placeholder='First Name'
                        onChange={handleInputChange}
                    />
                </div>

                <div className='form-group nice'>
                    <label>Last Name:</label>
                    <input
                        type='text'
                        className='form-control'
                        value={form.lastname}
                        name='lastname'
                        placeholder='Last Name'
                        onChange={handleInputChange}
                    />
                </div>

                <div className='form-group nice'>
                    <label>Email:</label>
                    <input
                        type='email'
                        className='form-control'
                        value={form.email}
                        name='email'
                        placeholder='Email'
                        onChange={handleInputChange}
                    />
                </div>

                <div className='form-group nice'>
                    <label>Phone:</label>
                    <input
                        type='text'
                        className='form-control'
                        value={form.phone}
                        name='phone'
                        placeholder='Phone Number'
                        onChange={handleInputChange}
                    />
                </div>
                <div style={{ alignItems: 'center' }} className='form-group nice flex items-center'>
                    <label style={{ flexBasis: '300px' }}>Quarterly Email</label>
                    <Switch
                        checked={form.quarterlyReportEnabled}
                        onChange={handleSwitchChange}
                        name='quarterlyReportEnabled'
                        aria-label='Toggle quarterly email notifications'
                    />
                </div>
                <div style={{ alignItems: 'center' }} className='form-group nice flex items-center'>
                    <label style={{ flexBasis: '300px' }}>Make as Super Admin</label>
                    <Switch
                        checked={form.isSuperAdmin}
                        onChange={handleSwitchChange}
                        name='isSuperAdmin'
                        aria-label='Toggle super admin status'
                    />
                </div>

                {hasSSo ? null : <div style={{ alignItems: 'center' }} className='form-group nice flex items-center'>
                    <label style={{ flexBasis: '300px' }}>Two Factor Authentication</label>
                    <Switch
                        checked={form.is2FAEnabled}
                        onChange={handleSwitchChange}
                        disabled={!props.form.selectedUser?.is2FAEnabled}
                        name='is2FAEnabled'
                        aria-label='Toggle 2fa status'
                    />
                </div>}

                <div>Select Feins:</div>

                <div className='row'>
                    {(props.feins ?? []).map((each, index) => (
                        <div key={`company-${index}`} className='col-12'>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={feins.includes(each.employer_fein.toString())}
                                        onChange={() => selectFeins(each.employer_fein)}
                                    />
                                }
                                label={each.employer_name}
                            />
                        </div>
                    ))}
                </div>
            </DialogContent>

            <DialogActions>
                <Button disabled={busy} variant='outlined' onClick={add} color='primary'>
                    {isEditing ? 'Update Employer' : 'Create Employer'}
                </Button>

                <Button onClick={() => props.toggleForm(null)} color='primary'>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <>
            <Tooltip title='Add new employer admin' placement='top' className='floating-button'>
                <Fab color='primary' onClick={() => props.toggleForm()}>
                    <Add />
                </Fab>
            </Tooltip>

            {props.form.showForm && renderAddForm()}
        </>
    );
}

export default connect(null, { showError, showMessage, confirmAction })(CreateEmployerAdmin);
